import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  SafeMindsLogo,
  useTheme,
} from '@safe-minds-ai/ui';
import { useState } from 'react';
import { getAccessToken, loginAdmin } from '../api';
import { useToast } from '../toast';

export default function LoginRoute(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [pending, setPending] = useState(false);
  const authed = Boolean(getAccessToken());
  const toast = useToast();
  const { resolvedTheme } = useTheme();
  const logoVariant = resolvedTheme === 'dark' ? 'full-dark' : 'full-light';

  if (authed) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const email = String(form.get('email') || '');
    const password = String(form.get('password') || '');
    setPending(true);
    try {
      await loginAdmin(email, password);
      toast.push({ title: 'Success', description: 'Logged in successfully!', variant: 'success' });
      const dest = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(dest, { replace: true });
    } catch (err: unknown) {
      let message = 'Login failed. Check your credentials.';
      if (err instanceof Error && 'response' in err && (err as any).response?.data?.message) {
        message = (err as any).response.data.message;
      }
      toast.push({ title: 'Error', description: message, variant: 'error' });
    } finally {
      setPending(false);
    }
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center 
     bg-[radial-gradient(circle_at_top,_hsl(var(--primary)/0.18),_hsl(var(--background))_65%)]
     px-4"
    >
      <Card className="w-full max-w-md border border-slate-100 bg-card/95 shadow-2xl dark:border-slate-700/40 dark:bg-slate-900/85">
        <CardHeader className="space-y-4 text-center">
          {/* Bigger banner-style logo with text */}
          <div className="mx-auto inline-flex flex-col items-center gap-2">
            <SafeMindsLogo variant={logoVariant} size={220} className="mx-auto" />
            <span className="rounded-full bg-primary/5 px-3 py-1 text-xs font-medium uppercase tracking-[0.18em] text-primary">
              Admin Console
            </span>
          </div>

          <div className="space-y-1">
            <CardTitle className="text-xl md:text-2xl">Sign in</CardTitle>
            <CardDescription className="text-sm text-slate-500 dark:text-slate-300">
              Use your admin credentials to access dashboards, flags, and
              controls.
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="admin@safeminds.ai"
                autoComplete="email"
                required
                className="dark:border-slate-600 dark:bg-slate-900/80 dark:text-slate-100 dark:placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-blue-500/50 focus-visible:border-blue-400"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="••••••••"
                autoComplete="current-password"
                required
                className="dark:border-slate-600 dark:bg-slate-900/80 dark:text-slate-100 dark:placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-blue-500/50 focus-visible:border-blue-400"
              />
            </div>

            <Button
              type="submit"
              className="mt-1 w-full rounded-full text-sm md:text-base"
              disabled={pending}
            >
              {pending ? 'Signing in…' : 'Sign in'}
            </Button>

            <p className="pt-2 text-center text-xs text-slate-500 dark:text-slate-300">
              Need access?{' '}
              <Link to="/support" className="text-primary hover:underline">
                Contact support
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
